// A1-100 (operator 2026-10-25, a real run on "UK TEST Data 2": "interface is
// extremely confusing. No clear directions"; "additional flags which I don't
// even know how to deal with"). The queue was a long run of cards with no
// order and no single next step. This is the short list at its top: every
// blocking card first, then the ones that need an answer, largest first,
// each in one line with how many rows are left and the one thing to do;
// information-only cards folded into one line. Same words as the cards
// themselves (flag_titles; the "what to do" from cardInstruction, A2-637),
// so the list never disagrees with the card it opens.
//
// A2-579 (operator 2026-10-27, "like Las Vegas ... too much bright light";
// design approved the same day, ~/Applications/step4-review-mockup.html):
// the list IS the page — calm rows with one small dot, colour only for what
// stops filing; a setting gap shown as ONE fix, not thousands of rows; notes
// and answered items folded.
import React from "react";
import { useTranslation } from "react-i18next";
import { GoToAnswer } from "./AnswerNotes";
import { cardInstruction } from "./cardInstruction";   // A2-637 Owner B
import { countryName } from "../../lib/countries";

const left = (g) => Math.max(0, (g.count || 0) - (g.acknowledged_count || 0));

/** "blocking" | "decide" | "info" | null (nothing left to do). */
// A1-103 (operator 2026-10-31): the server says what each card asks (g.tier:
// must / check / info, api/_review_taxonomy.tier_of); a note never needs a tick.
export const tierOf = (g) => {
  if (g.family === "REPLAYED") return null;
  if (g.tier === "info" || (!g.tier && g.audit_only)) return "info";
  if (left(g) === 0) return null;
  // A2-640: a Family B card whose open rows all belong to a country with no return here
  if (g.family === "B" && g.off_period_only && g.tier !== "must") return "elsewhere";
  return g.tier === "must" || g.family === "B" ? "blocking" : "decide";
};

// A2-640 (operator 2026-11-03): "belongs to Germany, answer in the German
// monthly calculation" — one line per country, from the server's count
const OffPeriod = ({ rows, t }) => Object.entries(rows || {}).map(([cc, r]) => {
  const name = countryName(cc) || cc;
  const cadence = r.cadence ? t(`ui.review_queue.cadence_${r.cadence}`, { defaultValue: r.cadence }) : "";
  return (
    <li key={cc} data-testid={`to-file-off-period-${cc}`}
        className="grid grid-cols-[10px_1fr] items-center gap-3 px-4 py-2.5 border-t border-slate-200 dark:border-slate-800">
      <span aria-hidden className="w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600" />
      <span className="text-sm text-slate-800 dark:text-slate-100">
        {cadence
          ? t("ui.review_queue.off_period_line", { count: r.rows, country: name, cadence,
              defaultValue: `${r.rows} row(s) belong to ${name}: answer them in ${name}'s ${cadence} calculation. They do not stop this return.` })
          : t("ui.review_queue.off_period_line_plain", { count: r.rows, country: name,
              defaultValue: `${r.rows} row(s) belong to ${name}: answer them in ${name}'s own calculation. They do not stop this return.` })}
      </span>
    </li>
  );
});

// A2-582 — a card that one change in the client record, Settings or Step 2
// clears for every row on the next re-run: the server's answer says so
// (g.bulk_answers GO_TO), where a hand list of flags used to. The words for
// a card that has its own are in ui.review_queue.setting_fix.<flag>.
export const settingFix = (g) => (g.bulk_answers || []).find((a) => a.kind === "GO_TO") || null;

const DOT = {
  blocking: "bg-orange-600 dark:bg-orange-300",
  decide: "bg-amber-600/70 dark:bg-amber-300/70",
  info: "bg-slate-400",
};

// A2-598 — the row whose card is open below is shown as selected, in the same
// faint wash the card takes, so the two read as one (never bright: A2-579).
const SELECTED = {
  blocking: "bg-orange-50/50 dark:bg-orange-950/20 border-l-[3px] border-l-orange-600/60 dark:border-l-orange-400/50",
  decide: "bg-amber-50/40 dark:bg-amber-950/15 border-l-[3px] border-l-amber-600/45 dark:border-l-amber-400/40",
  info: "bg-slate-50/70 dark:bg-slate-800/30 border-l-[3px] border-l-slate-400",
};

const Row = ({ g, tier, onOpen, clientId, t, selected }) => {
  // A1-105 (operator 2026-11-01): the period card is about the whole upload;
  // its one row only carries the note, so no row count and no "clears N rows"
  const whole = !!g.period_note;
  const fix = settingFix(g);   // its way out ("Check the files in Step 2") stays
  const n = left(g);
  const isSelected = selected === g.flag_type;
  return (
    <li data-testid={`to-file-${g.flag_type}`} data-tier={tier} aria-current={isSelected ? "true" : undefined}
        className={`grid grid-cols-[10px_1fr_auto_auto] items-center gap-3 px-4 py-2.5 border-t border-slate-200 dark:border-slate-800 ${isSelected ? SELECTED[tier] : ""}`}>
      <span aria-hidden className={`w-2 h-2 rounded-full ${DOT[tier]}`} />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
          {t(`ui.review_queue.setting_fix.${g.flag_type}.what`, {
            defaultValue: t(`flag_titles.${g.flag_type}`, { defaultValue: g.flag_type }) })}
        </span>
        <span className="block text-xs text-slate-500 dark:text-slate-400">
          {fix && !whole
            ? t(`ui.review_queue.setting_fix.${g.flag_type}.how`, { n, defaultValue: t("ui.review_queue.setting_fix_how", {
                n, defaultValue: "One change clears all {{n}} rows when you re-run." }) })
            : cardInstruction(g, t)}
        </span>
      </span>
      <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400 whitespace-nowrap">
        {!whole && t("ui.review_queue.to_file_rows", { n, defaultValue: `${n} row(s)` })}
      </span>
      <span className="flex items-center gap-2">
        {fix && <span data-testid={`to-file-fix-${g.flag_type}`}><GoToAnswer answer={fix} clientId={clientId} /></span>}
        <button type="button" data-testid={`to-file-open-${g.flag_type}`} onClick={() => onOpen && onOpen(g.flag_type)}
          className={tier === "blocking"
            ? "text-xs px-3 py-1 rounded-md bg-teal-700 text-white"
            : "text-xs px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100"}>
          {tier === "blocking"
            ? t("ui.review_queue.to_file_answer", { defaultValue: "Answer" })
            : t("ui.review_queue.to_file_open", { defaultValue: "Open →" })}
        </button>
      </span>
    </li>
  );
};

const Section = ({ label, items, extra = null, ...rest }) => (items.length || extra ? (
  <>
    <li aria-hidden className="px-4 pt-3 pb-1 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 list-none">
      {label} · {items.length + (extra ? 1 : 0)}
    </li>
    {items.map(({ g, tier }) => <Row key={g.flag_type} g={g} tier={tier} {...rest} />)}
    {extra}
  </>
) : null);

// A2-637 Owner D (card map D-1, operator's Step 4 check 2026-11-02: "47 rows
// [waiting] but at step 4 I can't see a card"): credit notes held for a sale
// no card of this calculation answers. Said here, with where to answer it.
const WaitingElsewhere = ({ w, t }) => {
  if (!w || !w.rows) return null;
  const sales = w.sales || [];
  // one phrase per kind of place present (review of f7943d2d: a mixed set
  // was all called "another upload")
  const uploads = [...new Set(sales.map((s) => s.upload).filter(Boolean))];
  const phrases = [];
  if (uploads.length) {
    phrases.push(t("ui.review_queue.waiting_upload", { uploads: uploads.map((u) => `“${u}”`).join(", "),
      defaultValue: `in another upload (${uploads.map((u) => `“${u}”`).join(", ")}): answer the sale in that upload's calculation, then re-run this one` }));
  }
  if (sales.some((s) => !s.upload && s.earlier)) {
    phrases.push(t("ui.review_queue.waiting_earlier", { defaultValue: "in an earlier period: answer the sale in its own period's calculation, then re-run this one" }));
  }
  if (!sales.length || sales.some((s) => !s.upload && !s.earlier)) {
    phrases.push(t("ui.review_queue.waiting_other", { defaultValue: "not on this calculation's returns: answer the sale where it is, then re-run this one" }));
  }
  const where = phrases.join("; ");
  return (
    <li data-testid="to-file-waiting-elsewhere" data-tier="blocking"
        className="grid grid-cols-[10px_1fr_auto] items-center gap-3 px-4 py-2.5 border-t border-slate-200 dark:border-slate-800">
      <span aria-hidden className={`w-2 h-2 rounded-full ${DOT.blocking}`} />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
          {t("ui.review_queue.waiting_title", { count: w.rows,
            defaultValue: `${w.rows} credit notes wait for their sale` })}
        </span>
        <span className="block text-xs text-slate-500 dark:text-slate-400">
          {t("ui.review_queue.waiting_where", { where, defaultValue: `The sale is ${where}.` })}
        </span>
      </span>
      <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400 whitespace-nowrap">
        {t("ui.review_queue.to_file_rows", { n: w.rows, defaultValue: `${w.rows} row(s)` })}
      </span>
    </li>
  );
};

export const ToFileList = ({ groups = [], onOpen, clientId = null, selected = null, waitingElsewhere = null,
                            offPeriodRows = null }) => {
  const { t } = useTranslation();
  const open = groups.map((g) => ({ g, tier: tierOf(g) })).filter((x) => x.tier);
  const bySize = (a, b) => left(b.g) - left(a.g);
  const stops = open.filter((x) => x.tier === "blocking").sort(bySize);
  // A note that names a setting (audit-only) stays a note; only a card that
  // asks for an answer is listed as a setting to fix.
  const settings = open.filter((x) => x.tier === "decide" && settingFix(x.g)).sort(bySize);
  const checks = open.filter((x) => x.tier === "decide" && !settingFix(x.g)).sort(bySize);
  const elsewhere = open.filter((x) => x.tier === "elsewhere").sort(bySize);
  const offRows = offPeriodRows && Object.keys(offPeriodRows).length ? offPeriodRows : null;
  // notes, and groups decided in the calc this one replaced — every card stays reachable
  const info = [...open.filter((x) => x.tier === "info"),
    ...groups.filter((g) => g.family === "REPLAYED" && (g.count || 0) > 0).map((g) => ({ g, tier: "info" }))];
  const resolved = groups.filter((g) => g.family !== "REPLAYED" && (g.count || 0) > 0 && left(g) === 0);
  const rest = { onOpen, clientId, t, selected };
  // A1-103: the notes stay one click away under (i), even with nothing left to do.
  const infoBlock = info.length > 0 && (
        <details data-testid="to-file-info" className="border-t border-slate-200 dark:border-slate-800 px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
          <summary className="cursor-pointer">
            {t("ui.review_queue.to_file_info", { n: info.length, defaultValue: `ⓘ ${info.length} note(s) — nothing to do` })}
          </summary>
          <ul className="mt-1">
            {info.map(({ g }) => (
              <li key={g.flag_type}>
                <button type="button" className="underline" data-testid={`to-file-info-open-${g.flag_type}`}
                        onClick={() => onOpen && onOpen(g.flag_type)}>
                  {t(`flag_titles.${g.flag_type}`, { defaultValue: g.flag_type })}
                </button>{!g.period_note && <>{" · "}{g.count}</>}
              </li>
            ))}
          </ul>
        </details>
  );
  const waiting = waitingElsewhere && waitingElsewhere.rows > 0 ? waitingElsewhere : null;
  const offBlock = (elsewhere.length || offRows) ? (
    <Section label={t("ui.review_queue.section_elsewhere", { defaultValue: "Answered in another calculation" })}
             items={elsewhere} extra={offRows ? <OffPeriod rows={offRows} t={t} /> : null} {...rest} />
  ) : null;
  if (!stops.length && !settings.length && !checks.length && !waiting) {
    return (
      <div data-testid="to-file-list" className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 text-sm text-slate-800 dark:text-slate-100">
        ✓ {t("ui.review_queue.to_file_none", { defaultValue: "Nothing left to answer on this step. Go on to Step 5 to sign off." })}
        {offBlock && <ul className="mt-2 -mx-4">{offBlock}</ul>}
        {infoBlock}
      </div>
    );
  }
  return (
    <div data-testid="to-file-list" className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="px-4 pt-3 text-base font-semibold text-slate-900 dark:text-slate-100">
        {t("ui.review_queue.to_file_title", { defaultValue: "To file this return" })}
      </div>
      <div className="px-4 pb-2 text-xs text-slate-500 dark:text-slate-400">
        {t("ui.review_queue.to_file_saved", { defaultValue: "Every answer is saved as you give it and survives a re-run. Give your answers, then re-run once." })}
      </div>
      <ul className="pb-1">
        <Section label={t("ui.review_queue.section_stops", { defaultValue: "Stops filing" })} items={stops}
                 extra={waiting ? <WaitingElsewhere w={waiting} t={t} /> : null} {...rest} />
        <Section label={t("ui.review_queue.section_settings", { defaultValue: "One setting clears many rows" })} items={settings} {...rest} />
        <Section label={t("ui.review_queue.section_check", { defaultValue: "Worth checking" })} items={checks} {...rest} />
        {offBlock}
      </ul>
      {infoBlock}
      {resolved.length > 0 && (
        <details data-testid="to-file-resolved" className="border-t border-slate-200 dark:border-slate-800 px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
          <summary className="cursor-pointer">
            ✓ {t("ui.review_queue.to_file_resolved", { n: resolved.length, defaultValue: `${resolved.length} answered` })}
          </summary>
          <ul className="mt-1">
            {resolved.map((g) => (
              <li key={g.flag_type}>
                ✓ <button type="button" className="underline" data-testid={`to-file-resolved-open-${g.flag_type}`}
                          onClick={() => onOpen && onOpen(g.flag_type)}>
                  {t(`flag_titles.${g.flag_type}`, { defaultValue: g.flag_type })}
                </button>{!g.period_note && <> · {g.count}</>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default ToFileList;
